import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { categoryService } from "@/services/categoryService";
import { faqService } from "@/services/faqService";
import { CategoryEntityType } from "@/types/category";
import type { FAQ } from "@/types/faq";

interface AddFaqDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (faq: FAQ) => void;
  hotelId: string;
  existingIds: string[];
}

export const AddFaqDialog: React.FC<AddFaqDialogProps> = ({
  open,
  onOpenChange,
  onAdd,
  hotelId,
  existingIds,
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open) {
      setSearch("");
    }
  }, [open]);

  const { data: faqs = [], isLoading } = useQuery({
    queryKey: ["mobile-settings", "faq-options", hotelId],
    enabled: open && hotelId.length > 0,
    queryFn: async (): Promise<FAQ[]> => {
      const categoriesResponse = await categoryService.findAll({
        entityType: CategoryEntityType.FAQ,
        hotelId,
        limit: 500,
        page: 1,
      });

      const categories = categoriesResponse.items.filter(
        (category) => !category.isArchived,
      );

      const faqResponses = await Promise.all(
        categories.map((category) =>
          faqService.getAll({
            categoryId: category.id,
            hotelId,
            limit: 500,
            page: 1,
          }),
        ),
      );

      return faqResponses.flatMap((response, index) =>
        response.items
          .filter((faq) => !faq.isArchived)
          .map((faq) => ({
            ...faq,
            category: {
              id: categories[index].id,
              name: categories[index].name,
            },
          })),
      );
    },
    staleTime: 60_000,
  });

  const filteredFaqs = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return faqs.filter((faq) => {
      if (existingIds.includes(faq.id)) {
        return false;
      }

      if (normalizedSearch.length === 0) {
        return true;
      }

      return (
        faq.question.toLowerCase().includes(normalizedSearch) ||
        faq.answer.toLowerCase().includes(normalizedSearch) ||
        faq.category?.name?.toLowerCase().includes(normalizedSearch) === true
      );
    });
  }, [existingIds, faqs, search]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t(
              "mobileSettings.dashboard.addRecommendedFaq",
              "Add Recommended FAQ",
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-zinc-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t(
                "mobileSettings.dashboard.searchFaqs",
                "Search FAQs...",
              )}
              className="pl-9"
            />
          </div>

          <div className="custom-scrollbar h-[400px] overflow-y-auto pr-4">
            {isLoading ? (
              <div className="py-8 text-center text-zinc-500">
                {t("mobileSettings.dashboard.loadingFaqs", "Loading FAQs...")}
              </div>
            ) : null}

            {!isLoading && filteredFaqs.length === 0 ? (
              <div className="py-8 text-center text-zinc-500">
                {t("mobileSettings.dashboard.noFaqsFound", "No FAQs found")}
              </div>
            ) : null}

            {!isLoading && filteredFaqs.length > 0 ? (
              <div className="grid grid-cols-2 gap-3">
                {filteredFaqs.map((faq) => (
                  <div
                    key={faq.id}
                    className="flex cursor-pointer flex-col gap-2 rounded-xl border bg-zinc-50/50 p-3 transition-colors hover:border-zinc-900"
                    onClick={() => {
                      onAdd(faq);
                      onOpenChange(false);
                    }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      {faq.category?.name ? (
                        <Badge
                          variant="secondary"
                          className="bg-zinc-200 px-2 py-0 text-[10px] text-zinc-900"
                        >
                          {faq.category.name}
                        </Badge>
                      ) : null}
                    </div>
                    <span className="line-clamp-2 text-sm font-medium">
                      {faq.question}
                    </span>
                    <span className="line-clamp-3 text-xs text-zinc-500">
                      {faq.answer}
                    </span>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
